import ConnectedContainer from "components/container/ConnectedContainer.tsx";
import {Form, Formik} from "formik";
import {tryAsyncFunc} from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import {authActions} from "config/store/modules/auth.ts";
import LoadingContainer from "components/container/LoadingContainer.tsx";
import EventForm from "components/layouts/form/EventForm.tsx";
import Button from "components/atom/button/Button.tsx";
import {useCallback, useEffect, useMemo, useState} from "react";
import {EventStatType, EventType} from "globals/types/models.ts";
import {useNavigate, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "config/store";
import {useTranslation} from "react-i18next";
import {useMount} from "react-use";
import {EventFormValuesType, getValuesFromEvent} from "utils/form.ts";
import dayjs from "dayjs";
import ConfirmActionModal from "components/modals/ConfirmActionModal.tsx";
import {USER_PLAN_TYPES} from "globals/constants.ts";
import EventStatsChart from "components/layouts/EventStatsChart.tsx";
import TabbedButtons from "components/layouts/TabbedButtons.tsx";


function ModifyEventPage() {
    const {t} = useTranslation()
    const params = useParams()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const myEvents: EventType[] = useAppSelector(state => state.auth.myEvents)
    const currentUser = useAppSelector(state => state.auth.user)
    const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false)

    const [tab, setTab] = useState<'infos' | 'stats'>('infos')

    const [eventStats, setEventStats] = useState<EventStatType[]>([])
    const [event, setEvent] = useState<EventType | null>(null)


    useMount(async () => {
        if (!params.eventUuid) {
            navigate('/profile')
            return

        }

        const eventFromArr = myEvents.find(e => e.uuid === params.eventUuid as string) || null

        if (eventFromArr) {
            setEvent(eventFromArr)
            return
        }

        const [eventFromReq, err] = await tryAsyncFunc(dispatch(authActions.getMyEventByUuid(params.eventUuid as string)))

        if (err) {
            navigate('/profile')
            return
        }

        setEvent(eventFromReq)
    })

    useEffect(() => {
        if (!event) return
        ajax.get(`/connected/events/${event.id}/stats`)
            .then((res) => {
                setEventStats(res.data.eventStats)
            })
    }, [event])


    const isPastEvent = useMemo(() => {
        if (!event) return false
        if (event.dates.length === 0) return true
        //use toSorted since .sort mutate the original array
        const latestDate = event.dates.toSorted((a, b) => {
            return dayjs(a.startDate).isBefore(dayjs(b.startDate), 'minutes') ? 1 : -1
        })[0]

        return dayjs(latestDate.endDate).isBefore(dayjs(), 'minutes')
    }, [event])

    const isDuring = useMemo(() => {
        if (!event) return false
        if (event.dates.length === 0) return false

        const now = dayjs()

        for (const date of event.dates) {
            if (dayjs(date.startDate).isBefore(now, 'minutes') && dayjs(date.endDate).isAfter(now, 'minutes')) {
                return true
            }
        }
        return false
    }, [event])

    const canEditEvent = useMemo(() => {
        if (!event) return true
        if (!event.active) return false

        if (isDuring) {
            if (currentUser?.planType === USER_PLAN_TYPES.COMPLETE_MONTHLY) return true//complete can edit even in pass
          if (currentUser?.planType === USER_PLAN_TYPES.COMPLETE_YEARLY) return true//complete can edit even in pass
            if (currentUser?.planType === USER_PLAN_TYPES.FREE_COMPLETE) return true//complete can edit even in pass
            return false
        }
        if (isPastEvent) {
            if (currentUser?.planType === USER_PLAN_TYPES.COMPLETE_MONTHLY) return true//complete can edit even in pass
          if (currentUser?.planType === USER_PLAN_TYPES.COMPLETE_YEARLY) return true//complete can edit even in pass
            if (currentUser?.planType === USER_PLAN_TYPES.FREE_COMPLETE) return true//complete can edit even in pass
            return false
        }
        if (event.dates.length === 0) return true

        return true
    }, [isDuring, currentUser, isPastEvent, event])

    const deleteEvent = useCallback(async () => {
        if (!event) return

        //allow to delete event if it's passed
        if (!confirmDeleteModal) {
            setConfirmDeleteModal(true)
            return
        }
        setConfirmDeleteModal(false)//close modal


        const [_, err] = await tryAsyncFunc(ajax.delete(`/connected/events/id/${event.id}`))

        if (err) {
            return
        }

        await dispatch(authActions.deletedEvent(event.id))
        navigate('/profile')
    }, [confirmDeleteModal, dispatch, event, navigate])


    const detailEventStats = useMemo(() => {
        const data = {
            viewCount: 0,
            btnClickCount: 0
        }

        eventStats.forEach((eventStat: EventStatType) => {
            if (eventStat.type === 'view') {
                data.viewCount += eventStat.count
            }
            if (eventStat.type === 'btn_click') {
                data.btnClickCount += eventStat.count
            }
        })
        return data
    }, [eventStats])

    if (!event) {
        return null
    }

    return (
        <ConnectedContainer title={t('event.modify')}>
            <div className="mb-6">
            <TabbedButtons
                onClick={(tab) => setTab(tab as 'infos' | 'stats')}
                selectedValue={tab}
                buttons={[
                    {label: t('informations'), value: 'infos'},
                    {label: t('stats'), value: 'stats'}
                ]}
            />
            </div>
            <div className="w-full" style={{display: tab === 'stats' ? 'block' : 'none'}}>
                <div>
                    <p>
                        {t('event.signaledCount')}: {event.signaledCount}</p>
                    <p>
                        {t('event.totalNumberOfViews')}: {detailEventStats.viewCount}
                    </p>
                    <p>
                        {t('event.totalNumberOfClicks')}: {detailEventStats.btnClickCount}
                    </p>

                    <EventStatsChart eventStats={eventStats}/>
                </div>
            </div>
            <div className="w-full" style={{display: tab === 'infos' ? 'block' : 'none'}}>
                <Formik
                    initialValues={getValuesFromEvent(event)}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validate={values => {
                        const errors: Partial<{ [K in keyof EventFormValuesType]: string }> = {};//same keys as type but with string value
                        if (!values.titleFr) {
                            errors.titleFr = t('forms.errors.requiredTitleFr')
                        }
                        return errors;
                    }}
                    onSubmit={async (values, {setSubmitting, setStatus, setFieldError, setValues}) => {
                        if (!canEditEvent) return
                        setSubmitting(true);
                        setStatus(null);

                        const [res, err] = await tryAsyncFunc(ajax.put(`/connected/events/${event.id}`, values))
                        setSubmitting(false);

                        if (err) {
                            const errData = err.response.data || {}
                            const errors = errData.errors || {}

                            if (errors.global) {
                                setStatus(errors.global)
                                delete errors.global
                            }

                            Object.keys(errors).forEach(errKey => {
                                setFieldError(errKey, errors[errKey])
                            })
                            return
                        }

                        setEvent(res.data.event)
                        setValues(getValuesFromEvent(res.data.event))

                        dispatch(authActions.updatedEvent(res.data.event))
                    }}
                >
                    {({isSubmitting, status}) => (
                        <LoadingContainer isLoading={isSubmitting} keepChildren>
                            <Form>
                                {isPastEvent && !canEditEvent && (
                                    <p className="block text-red-500">
                                        {t('event.isPast')}
                                    </p>
                                )}
                                {!event.active && (
                                    <p className="block text-red-500">
                                        {t('event.isInactive')}
                                    </p>
                                )}
                                {event?.signaledCount > 0 && (
                                    <p className="block text-red-500">
                                        {t('event.isSignal')}
                                    </p>
                                )}
                                <EventForm disabled={!canEditEvent}/>

                                {status && (
                                    <p className="error block">{status}</p>
                                )}
                                <div className="mt-4 flex w-full flex-col items-center justify-center gap-4">
                                    <Button className="w-full" type="submit" disabled={!canEditEvent || isSubmitting}>
                                        {t('saveModifications')}
                                    </Button>
                                    <Button style="text-red" className="inline-block" type="button"
                                            disabled={isSubmitting}
                                            onClick={deleteEvent}>
                                        {t('event.delete')}
                                    </Button>
                                </div>
                            </Form>
                        </LoadingContainer>
                    )}
                </Formik>
            </div>

            <ConfirmActionModal
                isOpen={confirmDeleteModal}
                onClose={() => setConfirmDeleteModal(false)}
                onAccept={deleteEvent}
            >
              <p className="text-center text-gray-400">
                    {t('event.confirmDelete')}
                </p>
            </ConfirmActionModal>
        </ConnectedContainer>
    )
}

export default ModifyEventPage
